(() => {
  // ===== Config =====
  // Your Railway backend base URL. It exposes:
  //   GET /api/geo           -> zone from the caller's IP (server-side)
  //   GET /api/geo?zip=NNNNN -> zone for a specific ZIP (manual entry)
  const GEO_ENDPOINT = "https://web-production-42e245.up.railway.app/api";

  const MASTER_COOKIE = "userLocationData"; // single source of truth
  const MAX_ZONE = 15; // <-- zones can go up to 15 (adjust if needed)

  const LINK_SELECTORS = [
    ".zone-detect-collection-card article a",
    ".search-zone-collection-card article a",
  ];

  // ===== Cookie helpers =====
  function setCookie(name, value, days = 7, { domain } = {}) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const parts = [
      `${name}=${encodeURIComponent(value ?? "")}`,
      `expires=${expires}`,
      "path=/",
      "SameSite=Lax",
      location.protocol === "https:" ? "Secure" : "",
      domain ? `domain=${domain}` : ""
    ].filter(Boolean);
    document.cookie = parts.join("; ");
  }

  function readCookie(name) {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  }

  // cross-subdomain cookie domain (e.g. .tnnursery.net)
  const APEX_DOMAIN = window.location.hostname.replace(/^www\./, "");
  const COOKIE_DOMAIN = APEX_DOMAIN.split(".").length >= 2 ? `.${APEX_DOMAIN}` : undefined;

  // ===== Utils =====
  const extractZoneNumber = (zoneStr) => {
    if (!zoneStr) return null;
    const m = String(zoneStr).match(/\d+/);
    if (!m) return null;
    const n = parseInt(m[0], 10);
    return Number.isFinite(n) ? n : null;
  };

  // clamp to valid range 1..MAX_ZONE (but don't coerce up from 0/NaN).trust-badge__sub
  const clampZone = (n) => (n == null ? null : (n < 1 ? 1 : n > MAX_ZONE ? MAX_ZONE : n));

  const canonicalShopAllUrl = (zoneDigit) => {
    const url = new URL("/collections/shop-all", location.origin);
    url.searchParams.set("filter.p.m.custom.planting_zone", String(zoneDigit));
    return url.toString();
  };

  const queryLinks = () =>
    LINK_SELECTORS.flatMap((sel) => [...document.querySelectorAll(sel)].map((a) => ({ sel, a })));

  // ===== Data source: your Railway backend =====
  // Returns { ip, zip, city, region, zone, source }. Pass a zip to look up a
  // specific ZIP (manual entry); omit it to geolocate by the caller's IP.
  async function fetchGeo(zip) {
    const url = new URL(GEO_ENDPOINT + "/geo");
    if (zip) url.searchParams.set("zip", zip);
    const res = await fetch(url.toString(), { credentials: "omit" });
    if (!res.ok) throw new Error(`geo endpoint ${res.status}`);
    return res.json();
  }

  // Kept the old name/shape so the rest of the code is unchanged. City/State
  // are null on the ZIP path (the zone lookup doesn't return them) — that's fine.
  async function getZoneByZip(zip) {
    const r = await fetchGeo(zip);
    return { City: r.city, State: r.region, Zipcode: r.zip, Zone: r.zone };
  }

  // ===== Persist + notify =====
  function persistMaster({ zoneDigit, zoneRaw, city, state, zip, region }) {
    const master = {
      v: 1,
      zone: zoneDigit,          // normalized digit (1..MAX_ZONE)
      zoneRaw: zoneRaw || null, // e.g., "6a" if upstream provides it
      city: city || null,
      state: state || null,
      zip: zip || null,
      region: region || state || null,
      popupClosed: false,
      ts: Math.floor(Date.now() / 1000)
    };
    setCookie(MASTER_COOKIE, JSON.stringify(master), 7, { domain: COOKIE_DOMAIN });

    // Notify the Liquid section immediately
    if (zoneDigit) {
      window.dispatchEvent(new CustomEvent("tn:zone-detected", {
        detail: {
          zone: `Zone ${zoneDigit}`,
          zoneNumber: zoneDigit
        }
      }));
    }
  }

  // ===== Reporting (console only) =====
  function buildReport(source, data) {
    const zoneNum = clampZone(extractZoneNumber(data.Zone));
    const shopAllUrl = zoneNum ? canonicalShopAllUrl(zoneNum) : null;

    const linkChanges = zoneNum
      ? queryLinks().map(({ sel, a }) => ({ selector: sel, from: a.href, to: shopAllUrl }))
      : [];

    return {
      source,
      city: data.City ?? null,
      state: data.State ?? null,
      zip: data.Zipcode ?? null,
      zoneRaw: data.Zone ?? null,
      zoneNumber: zoneNum,
      shopAllUrl,
      linkChanges,
    };
  }

  function logReport(report, label = "Plant Zone Detection") {
    console.group(label);
    console.table({
      Source: report.source,
      City: report.city,
      State: report.state,
      Zip: report.zip,
      "Zone (raw)": report.zoneRaw,
      "Zone number": report.zoneNumber,
      "Shop-all URL": report.shopAllUrl,
    });
    if (report.linkChanges.length) {
      console.groupCollapsed("Link updates (simulated → canonical shop-all URL)");
      report.linkChanges.forEach((c, i) => {
        console.log(`#${i + 1}`, c.selector, "\nfrom:", c.from, "\n  to:", c.to);
      });
      console.groupEnd();
    }
    console.groupEnd();
  }

  // ===== Detection flows =====
  // Try master cookie; if only legacy exists, migrate it into master.
  function detectFromCookiesAndMaybeMigrate() {
    // 1) Preferred master cookie
    const rawMaster = readCookie(MASTER_COOKIE);
    if (rawMaster) {
      try {
        const master = JSON.parse(rawMaster);
        if (Number.isFinite(master.zone)) {
          return {
            report: {
              source: "cookie:master",
              City: master.city, State: master.state, Zipcode: master.zip,
              Zone: master.zoneRaw ?? `Zone ${master.zone}`
            }
          };
        }
      } catch {}
    }

    // 2) Legacy cookie migration (if you still have a `location` cookie around)
    const rawLegacy = readCookie("location");
    if (rawLegacy) {
      try {
        const legacy = JSON.parse(rawLegacy); // { Zone, City, State, Zipcode, ... }
        const digit = clampZone(extractZoneNumber(legacy?.Zone));
        if (digit) {
          persistMaster({
            zoneDigit: digit,
            zoneRaw: legacy.Zone,
            city: legacy.City,
            state: legacy.State,
            zip: legacy.Zipcode,
            region: legacy.State
          });
          return { report: { source: "cookie:legacy→migrated", ...legacy } };
        }
      } catch {}
    }

    return { report: null };
  }

  async function detectFromIP() {
    const r = await fetchGeo();               // IP resolved server-side
    if (!r?.zip) return { error: "No ZIP from geo endpoint", r };
    if (!r?.zone) return { error: "No zone from geo endpoint", r };

    const digit = clampZone(extractZoneNumber(r.zone));
    if (digit) {
      persistMaster({
        zoneDigit: digit,
        zoneRaw: r.zone,
        city: r.city,
        state: r.region,
        zip: r.zip,
        region: r.region
      });
    }
    return buildReport("ip→zone", { City: r.city, State: r.region, Zipcode: r.zip, Zone: r.zone });
  }

  async function run() {
    try {
      const { report } = detectFromCookiesAndMaybeMigrate();
      if (report) {
        const rep = buildReport(report.source, report);
        logReport(rep, "Plant Zone (cookie)");
        return;
      }

      const ipRep = await detectFromIP();
      if ("error" in ipRep) {
        console.warn("Zone detect via IP failed:", ipRep);
        return;
      }
      logReport(ipRep, "Plant Zone (ip→zone)");
    } catch (err) {
      console.error("Zone detection error:", err);
    }
  }

  // Manual ZIP support (persist + notify)
  function wireManualLookup() {
    const btn = document.getElementById("zone-finder-change-zone-update");
    const input = document.querySelector(".zone-finder-change-zone-input");
    if (!btn || !input) return;
    btn.addEventListener("click", async () => {
      const zip = (input.value || "").trim();
      if (!/^\d{5}$/.test(zip)) {
        console.warn("Enter a valid 5-digit ZIP.");
        return;
      }
      try {
        const data = await getZoneByZip(zip);
        if (!data?.Zone) {
          console.warn("Couldn't determine zone for ZIP", zip, data);
          return;
        }
        const digit = clampZone(extractZoneNumber(data.Zone));
        if (digit) {
          persistMaster({
            zoneDigit: digit,
            zoneRaw: data.Zone,
            city: data.City,
            state: data.State,
            zip,
            region: data.State
          });
        }
        const report = buildReport("manual:zip", { ...data, Zipcode: zip });
        logReport(report, "Plant Zone (manual ZIP)");
      } catch (e) {
        console.error("Manual lookup failed:", e);
      }
    });
  }

  // ===== Init =====
  document.addEventListener("DOMContentLoaded", () => {
    run();
    wireManualLookup();
  });
})();
